import { observer } from 'mobx-react-lite';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { useCapturedAppRuntimeIcons } from '@/components/blocks/Message/messageSources/capturedAppIcons';
import { Text, Tooltip, XStack, YStack } from '@/components/ui';
import {
  integrationIconMap,
  normalizeToolkitKey,
} from '@/integrations/integration-icons';
import { cn } from '@/lib/utils';

import { LearningIconCycler } from './first-run-summary.icons';
import {
  FIRST_RUN_WINDOW_MS,
  firstRunSummaryStore,
} from './first-run-summary.store';

const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Time left in the ~2-minute learning window, ticked every second.
function useLearningCountdown(): string {
  const { cutoffDate } = firstRunSummaryStore;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = cutoffDate ? now - new Date(cutoffDate).getTime() : 0;
  return formatCountdown(FIRST_RUN_WINDOW_MS - elapsed);
}

// Captured-app tile: real OS icon → bundled brand icon → initial.
function CapturedAppIcon({
  app,
  iconDataUrl,
}: {
  app: string;
  iconDataUrl?: string | null;
}) {
  const Icon = integrationIconMap[normalizeToolkitKey(app)];
  return (
    <Tooltip label={app} side="top">
      <span className="inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded text-foreground-subtle">
        {iconDataUrl ? (
          <img src={iconDataUrl} alt="" className="size-full object-contain" />
        ) : Icon ? (
          <Icon className="size-3" />
        ) : (
          <span className="text-[length:calc(11rem/14)] font-medium uppercase leading-none">
            {app.trim().charAt(0) || '?'}
          </span>
        )}
      </span>
    </Tooltip>
  );
}

// First-run learning cluster: status, countdown, explanation, captured apps.
export const FirstRunLearningBanner = observer(
  ({ className }: { className?: string }) => {
    const captures = firstRunSummaryStore.recentCaptures;
    const countdown = useLearningCountdown();
    const reduceMotion = useReducedMotion();
    const appNames = useMemo(
      () => captures.map((capture) => capture.app),
      [captures]
    );
    const runtimeIcons = useCapturedAppRuntimeIcons(appNames);

    return (
      <div className={cn('relative w-fit max-w-md', className)}>
        {/* Pulsing green glow */}
        {reduceMotion ? (
          <div className="pointer-events-none absolute -left-6 -top-10 h-24 w-40 rounded-full bg-green-500/15 blur-2xl" />
        ) : (
          <motion.div
            className="pointer-events-none absolute -left-6 -top-10 h-24 w-40 rounded-full bg-green-500/15 blur-2xl"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 4.5, ease: 'easeInOut', repeat: Infinity }}
          />
        )}

        <YStack className="relative gap-2">
          <LearningIconCycler size={18} className="text-foreground-muted" />
          <XStack className="items-center gap-3">
            <Text className="min-w-0 truncate text-sm font-medium text-foreground">
              Learning about your work…
            </Text>
            <XStack className="shrink-0 items-center gap-1.5 text-foreground-muted">
              <Icons.clock className="size-3.5 shrink-0" />
              <Text className="text-xs tabular-nums text-foreground-muted">
                {countdown}
              </Text>
            </XStack>
          </XStack>

          <Text className="text-balance text-sm leading-normal text-foreground-muted">
            Keep using your apps as normal — a summary of what Littlebird picks
            up will show up in a new chat shortly.
          </Text>

          {captures.length > 0 && (
            <XStack className="items-center gap-2 pt-0.5">
              <Text className="shrink-0 text-xs text-foreground-muted">
                Reading from
              </Text>
              <XStack className="items-center gap-1">
                {captures.map((capture) => (
                  <CapturedAppIcon
                    key={capture.app}
                    app={capture.app}
                    iconDataUrl={runtimeIcons.get(capture.app)?.dataUrl}
                  />
                ))}
              </XStack>
            </XStack>
          )}
        </YStack>
      </div>
    );
  }
);

// Shown when the window elapses with nothing to summarize; dismiss settles first run.
export const FirstRunEmptyBanner = ({ className }: { className?: string }) => {
  return (
    <YStack className={cn('w-fit max-w-md gap-2', className)}>
      <Text className="text-sm font-medium text-foreground">
        Nothing to summarize yet
      </Text>
      <Text className="text-balance text-sm leading-normal text-foreground-muted">
        Littlebird keeps learning in the background as you work — start a chat
        whenever you're ready.
      </Text>
      <button
        type="button"
        onClick={() => firstRunSummaryStore.markDone()}
        className="w-fit pt-1 text-sm font-medium text-foreground-muted transition-colors hover:text-foreground"
      >
        Got it
      </button>
    </YStack>
  );
};
